import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import { useNavigate } from 'react-router-dom';

import { ApiObject, Program, School, Subject, CommonHash } from 'app/types';
import Text from 'common/components/Text/Text';
import styles from 'features/explore/school/SchoolDetail.module.scss';
import { useMediaQuery } from 'react-responsive';
import { navigateTop, schoolProgramLabelCard } from 'common/utils';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import {
  useAddFavoriteMutation,
  useDeleteFavoriteMutation,
  useGetSchoolProgramsQuery,
} from 'services/exploreApi';
import { formatNumber, newDetailUrl, scheduleConversion } from 'common/utils/conversions';
import { SessionState } from 'features/session/sessionSlice';
import { setFavorites, setSchoolShortlisted, UserState } from 'features/user/userSlice';
import TagManager from 'react-gtm-module';
import Pagination from 'common/components/Pagination/Pagination';

interface CompProps {
  school: ApiObject<School> | undefined;
  schoolId: number;
  logoSchool: string;
  studyRef?: React.RefObject<HTMLDivElement>;
  comesFromExplore?: boolean;
}

interface ProgramCardProps {
  program: Program;
  logoSchool: string;
}

interface PaginationInterface {
  pages: number;
  current: number;
  total: number;
}

const defaultPage: PaginationInterface = {
  pages: 0,
  current: 1,
  total: 0,
};

const ProgramCard = ({ program, logoSchool }: ProgramCardProps): React.ReactElement => {
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const label = schoolProgramLabelCard(program.preferences_score);
  const { logged } = useAppSelector(({ session }: { session: SessionState }) => session);
  const [isFavorite, setFavorite] = useState<boolean>(program.favorite ? program.favorite : false);
  const { favorites } = useAppSelector(({ user }: { user: UserState }) => user);
  const [addFavorite] = useAddFavoriteMutation();
  const [deleteFavorite] = useDeleteFavoriteMutation();

  const sendGTMEvent = (eventName: string) => {
    TagManager.dataLayer({
      dataLayer: {
        event: 'ga_event',
        eventName: eventName,
        programId: program.id,
        programName: program.name,
      },
    });
  };

  const navigateTo = (route: string) => {
    navigate(route);
    navigateTop();
  };

  const handleAddFavorite = () => {
    const values = { id: program?.id, kind: 'Program' };
    if (logged) {
      addFavorite(values).then(async (resp: any) => {
        setFavorite(true);
        if (!favorites?.includes(program.id)) {
          dispatch(setFavorites([...(favorites || []), program.id]));
        }
        dispatch(setSchoolShortlisted(true));
      });
      sendGTMEvent('program_shortlist');
    } else {
      setTimeout(() => {
        navigate('/signup');
      }, 100);
    }
  };
  const handleDeleteFavorite = () => {
    const values = { id: program?.id, kind: 'Program' };
    deleteFavorite(values).then(async (resp: any) => {
      setFavorite(false);
      dispatch(setFavorites(favorites?.filter((id) => id !== program.id)));
    });
    sendGTMEvent('program_unshortlist');
  };

  return (
    <div className={styles.programCard}>
      <div className="d-flex flex-row align-items-start">
        <div className="d-flex flex-row mb-2 col-12 justify-content-between align-items-start">
          <div className="d-flex flex-row gap-2 col-10">
            <div className={styles.logo}>
              <img src={logoSchool} alt={`Logo of ${program.name}`} className={styles.logo} />
            </div>
            <div
              className="d-flex flex-column col-10"
              onClick={() => navigateTo(newDetailUrl('programs', program.id, program.name))}
            >
              <Text
                size={isMobile ? 14 : 16}
                weight={600}
                inlineStyle={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  marginBottom: '4px',
                  color: '#19234F',
                }}
              >
                {program.name}
              </Text>
              <Text
                size={isMobile ? 12 : 14}
                weight={400}
                inlineStyle={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  color: '#19234F',
                }}
              >
                {program.school_name}
              </Text>
              {!isMobile && (
                <div className={styles.cardInfo}>
                  <div className={styles.data}>
                    <Text size={14} weight={600} inlineStyle={{ color: '#19234F' }}>
                      {program.many_study_options
                        ? `From ${`${program.tuition_min_currency} `}${formatNumber(
                            program.tuition_min
                          )},`
                        : `${`${program.study_options_first.tuition_fee?.currency} `}
                                ${formatNumber(
                                  program.study_options_first.tuition_fee?.amount_international
                                )}`}
                      &nbsp;
                    </Text>
                    <Text size={14} weight={600} inlineStyle={{ color: '#19234F' }}>
                      {`${
                        program.multiple_schedules
                          ? 'full time - part time'
                          : scheduleConversion(program.study_options_first.schedule)
                      },`}
                      &nbsp;
                    </Text>
                    <Text size={14} weight={600}>
                      {program.multiple_durations && 'from'}{' '}
                      {program.study_options_first.duration_in_months} months
                    </Text>
                  </div>
                  <div className={styles.location}>
                    <i className="bi bi-geo-alt"></i>
                    <Text size={isMobile ? 12 : 14} weight={400}>
                      {program.multiple_locations
                        ? program.online_delivery
                          ? 'Multiple and Online'
                          : 'Multiple'
                        : `${program.study_options_first.city.name}, ${program.study_options_first.city.country.name}`}
                    </Text>
                  </div>
                  {isMobile && (
                    <>
                      <hr />
                      <div
                        onClick={() =>
                          navigateTo(newDetailUrl('programs', program.id, program.name))
                        }
                        className={styles.detailButton}
                      >
                        See Details
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="d-flex flex-row gap-3">
            {label && !isMobile && (
              <div
                className={styles.labelContainer}
                style={{ backgroundColor: label.background, color: label.text }}
              >{`${label.title}`}</div>
            )}
            {isFavorite ? (
              <div
                className={styles.heart_fill}
                onClick={() => {
                  handleDeleteFavorite();
                }}
              >
                <i className="bi bi-heart-fill"></i>
              </div>
            ) : (
              <div
                className={styles.heart}
                onClick={() => {
                  handleAddFavorite();
                }}
              >
                <i className="bi bi-heart"></i>
              </div>
            )}
          </div>
        </div>
      </div>
      {isMobile && (
        <div className={styles.cardInfo}>
          {label && (
            <div
              className={styles.labelContainer}
              style={{ backgroundColor: label.background, color: label.text }}
            >{`${label.title}`}</div>
          )}
          <div className={styles.data}>
            <Text size={14} weight={600} inlineStyle={{ color: '#19234F' }}>
              {program.many_study_options
                ? `From ${`${program.tuition_min_currency} `}${formatNumber(program.tuition_min)}`
                : `${`${program.study_options_first.tuition_fee?.currency} `}
                                ${formatNumber(
                                  program.study_options_first.tuition_fee?.amount_international
                                )}`}
              &nbsp;
            </Text>
            <Text size={12} weight={400} inlineStyle={{ color: '#19234F' }}>
              {`${
                program.multiple_schedules
                  ? 'full time - part time'
                  : scheduleConversion(program.study_options_first.schedule)
              },`}
              &nbsp;
            </Text>
            <Text size={12} weight={400}>
              {program.multiple_durations && 'from'}{' '}
              {program.study_options_first.duration_in_months} months
            </Text>
          </div>
          <div className={styles.location}>
            <i className="bi bi-geo-alt"></i>
            <Text size={isMobile ? 12 : 14}>
              {program.multiple_locations
                ? program.online_delivery
                  ? 'Multiple and Online'
                  : 'Multiple'
                : `${program.study_options_first.city.name}, ${program.study_options_first.city.country.name}`}
            </Text>
          </div>
          {isMobile && (
            <>
              <hr />
              <div
                onClick={() => navigateTo(newDetailUrl('programs', program.id, program.name))}
                className={styles.detailButton}
              >
                See Details
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const StudySection = ({
  school,
  logoSchool,
  schoolId,
  studyRef,
  comesFromExplore,
}: CompProps): React.ReactElement => {
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const [programPagination, setProgramPagination] = useState<PaginationInterface>(defaultPage);
  const [activeSubject, setActiveSubject] = useState<string>('All');
  const [degreeType, setDegreeType] = useState<string>('All');
  const {
    data: programs,
    isLoading: isProgramsLoading,
    refetch,
  } = useGetSchoolProgramsQuery({
    id: schoolId,
    page: programPagination.current,
    subject: activeSubject,
    degreeType: degreeType,
  });
  const [subjectOptions, setSubjectOptions] = useState<{ label: string; value: string }[]>([
    { label: 'All', value: 'All' },
  ]);
  const [availableDegreeTypeOptions, setAvailableDegreeTypeOptions] = useState<string[]>([]);
  const [windowSize, setWindowSize] = useState([window.innerWidth, window.innerHeight]);

  const navigateUp = (windowOnly = false) => {
    if (isMobile || windowOnly) {
      navigateTop(isMobile ? 0 : 200);
    } else {
      navigateTop(200);
      navigateTop(0, 0, 'popular-programs-title');
    }
  };

  const changePagePrograms = (page: number) => {
    setProgramPagination((sp) => ({ ...sp, current: page }));
    navigateUp();
  };

  useEffect(() => {
    if (!isProgramsLoading && programs?.meta && programs.data.length > 0) {
      const array = [];
      if (programs?.meta.total_masters) array.push('masters');
      if (programs?.meta.total_bachelors) array.push('bachelors');
      setAvailableDegreeTypeOptions(array);
      setDegreeType(array[0]);
    }
  }, [isProgramsLoading]);

  useEffect(() => {
    if (programs?.meta) setProgramPagination(programs.meta);
  }, [programs]);

  useEffect(() => {
    const availableSubjects =
      degreeType == 'masters' ? programs?.meta.masters : programs?.meta.bachelors;
    const setupSubjectOptions = (subjects: CommonHash) => {
      const array = [];
      for (const [subject, value] of Object.entries(subjects)) {
        array.push({
          label: `${subject} (${value})`,
          value: subject,
        });
      }
      return array;
    };
    const arraySubjectOptions: { label: string; value: string }[] = [
      {
        label: `All (${
          degreeType === 'masters' ? programs?.meta.total_masters : programs?.meta.total_bachelors
        })`,
        value: 'All',
      },
      ...setupSubjectOptions(availableSubjects || {}),
    ];

    setSubjectOptions(arraySubjectOptions);
  }, [degreeType, programs]);

  useEffect(() => {
    refetch();
  }, [degreeType, activeSubject]);

  useEffect(() => {
    const handleWindowResize = () => {
      setWindowSize([window.innerWidth, window.innerHeight]);
    };
    window.addEventListener('resize', handleWindowResize);
    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, [windowSize]);

  return (
    <div
      className={styles.studySectionContainer}
      id="study"
      ref={studyRef}
      style={{ marginTop: comesFromExplore ? '32px' : isMobile ? '44px' : '80px' }}
    >
      {!comesFromExplore && (
        <div className={styles.sectionTitle}>
          <div className={styles.blueBlock} />
          <h2 className={styles.titleText}>Programs</h2>
        </div>
      )}
      <h3 className={styles.subtitle} style={{ fontSize: '18px' }} id="popular-programs-title">
        Popular programs
      </h3>
      <div className={`d-flex mt-2 ${isMobile ? 'flex-column gap-3' : 'gap-5'}`}>
        <div className={`${styles.degreeTypesContainer} row flex-row flex-nowrap pr-2`}>
          {availableDegreeTypeOptions.map((option, i) => {
            return (
              <div
                className={`${styles.degree} ${degreeType === option ? styles.activeDegree : ''}`}
                onClick={() => {
                  setDegreeType(option);
                  setActiveSubject('All');
                }}
                key={`${option}-${i}`}
              >
                {option === 'masters' ? 'Masters' : 'Bachelors'}
              </div>
            );
          })}
        </div>
        <div>
          <div className={`${styles.subjectsContainer} row flex-row flex-nowrap gap-3 pr-2`}>
            <div className="position-absolute p-0">
              <div className={styles.filtersFormInput}>
                <Select
                  hideSelectedOptions={false}
                  classNamePrefix="react-select"
                  isLoading={isProgramsLoading}
                  styles={{
                    control: (baseStyles: any) => ({
                      ...baseStyles,
                      background: 'white',
                      maxWidth: '300px',
                      height: '44px',
                      borderRadius: '30px',
                      border: '1px solid #0D6EFD',
                      paddingLeft: '1.5rem',
                      paddingRight: '1.5rem',
                      boxShadow: '0 0.125rem 0.25rem rgba(0, 0, 0, 0.075)',
                    }),
                    singleValue: (baseStyles: any) => ({
                      ...baseStyles,
                    }),
                    menu: (baseStyles: any) => ({
                      ...baseStyles,
                      maxWidth: '300px',
                    }),
                    indicatorSeparator: (baseStyles: any) => ({
                      ...baseStyles,
                      display: 'none',
                    }),
                  }}
                  options={subjectOptions}
                  onChange={(o) => {
                    setActiveSubject(String(o?.value) || 'All');
                  }}
                  value={
                    isProgramsLoading
                      ? { label: 'Loading...', value: -3 }
                      : subjectOptions.find((option) => option.value === activeSubject) ||
                        subjectOptions.find((option) => option.value === 'All')
                  }
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className={styles.sectionContent}>
        <div className={styles.programsContainer}>
          {programs?.data.map((program: Program, index: number) => (
            // me alega por duplicacion de keys y le agregue i. Revisar caso a futuro pq no debiesen estar rep
            <div key={`${program.id}-${index}`}>
              <ProgramCard program={program} logoSchool={logoSchool} />
            </div>
          ))}
        </div>

        <Pagination
          className={styles.pagination}
          {...programPagination}
          onChange={changePagePrograms}
        />
      </div>
    </div>
  );
};

export { StudySection, ProgramCard };
